import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { companyInfo, organizationSchema } from '../config/seoConfig';
import { pageSeoData } from '../config/pageSeoConfig';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaClock,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaPaperPlane,
  FaSearch,
  FaGlobe,
  FaBriefcase,
  FaCog,
  FaDollarSign,
  FaWhatsapp
} from 'react-icons/fa';
import './Contact.scss';

// Countries with codes
const countries = [
  { name: 'United States', code: '+1', flag: '🇺🇸' },
  { name: 'United Kingdom', code: '+44', flag: '🇬🇧' },
  { name: 'Canada', code: '+1', flag: '🇨🇦' },
  { name: 'Australia', code: '+61', flag: '🇦🇺' },
  { name: 'India', code: '+91', flag: '🇮🇳' },
  { name: 'Germany', code: '+49', flag: '🇩🇪' },
  { name: 'France', code: '+33', flag: '🇫🇷' },
  { name: 'Spain', code: '+34', flag: '🇪🇸' },
  { name: 'Italy', code: '+39', flag: '🇮🇹' },
  { name: 'Netherlands', code: '+31', flag: '🇳🇱' },
  { name: 'Belgium', code: '+32', flag: '🇧🇪' },
  { name: 'Switzerland', code: '+41', flag: '🇨🇭' },
  { name: 'Sweden', code: '+46', flag: '🇸🇪' },
  { name: 'Norway', code: '+47', flag: '🇳🇴' },
  { name: 'Denmark', code: '+45', flag: '🇩🇰' },
  { name: 'Finland', code: '+358', flag: '🇫🇮' },
  { name: 'Poland', code: '+48', flag: '🇵🇱' },
  { name: 'Austria', code: '+43', flag: '🇦🇹' },
  { name: 'Ireland', code: '+353', flag: '🇮🇪' },
  { name: 'Portugal', code: '+351', flag: '🇵🇹' },
  { name: 'Greece', code: '+30', flag: '🇬🇷' },
  { name: 'Czech Republic', code: '+420', flag: '🇨🇿' },
  { name: 'Romania', code: '+40', flag: '🇷🇴' },
  { name: 'Hungary', code: '+36', flag: '🇭🇺' },
  { name: 'Japan', code: '+81', flag: '🇯🇵' },
  { name: 'China', code: '+86', flag: '🇨🇳' },
  { name: 'South Korea', code: '+82', flag: '🇰🇷' },
  { name: 'Singapore', code: '+65', flag: '🇸🇬' },
  { name: 'Malaysia', code: '+60', flag: '🇲🇾' },
  { name: 'Thailand', code: '+66', flag: '🇹🇭' },
  { name: 'Indonesia', code: '+62', flag: '🇮🇩' },
  { name: 'Philippines', code: '+63', flag: '🇵🇭' },
  { name: 'Vietnam', code: '+84', flag: '🇻🇳' },
  { name: 'Hong Kong', code: '+852', flag: '🇭🇰' },
  { name: 'Taiwan', code: '+886', flag: '🇹🇼' },
  { name: 'New Zealand', code: '+64', flag: '🇳🇿' },
  { name: 'Brazil', code: '+55', flag: '🇧🇷' },
  { name: 'Mexico', code: '+52', flag: '🇲🇽' },
  { name: 'Argentina', code: '+54', flag: '🇦🇷' },
  { name: 'Chile', code: '+56', flag: '🇨🇱' },
  { name: 'Colombia', code: '+57', flag: '🇨🇴' },
  { name: 'Peru', code: '+51', flag: '🇵🇪' },
  { name: 'South Africa', code: '+27', flag: '🇿🇦' },
  { name: 'Nigeria', code: '+234', flag: '🇳🇬' },
  { name: 'Kenya', code: '+254', flag: '🇰🇪' },
  { name: 'Egypt', code: '+20', flag: '🇪🇬' },
  { name: 'UAE', code: '+971', flag: '🇦🇪' },
  { name: 'Saudi Arabia', code: '+966', flag: '🇸🇦' },
  { name: 'Qatar', code: '+974', flag: '🇶🇦' },
  { name: 'Kuwait', code: '+965', flag: '🇰🇼' },
  { name: 'Israel', code: '+972', flag: '🇮🇱' },
  { name: 'Turkey', code: '+90', flag: '🇹🇷' },
  { name: 'Russia', code: '+7', flag: '🇷🇺' },
  { name: 'Ukraine', code: '+380', flag: '🇺🇦' },
];

// Industries list
const industries = [
  'Technology & Software',
  'E-commerce & Retail',
  'Healthcare & Medical',
  'Finance & Banking',
  'Education & E-learning',
  'Real Estate & Property',
  'Travel & Hospitality',
  'Food & Beverage',
  'Fashion & Apparel',
  'Manufacturing & Industrial',
  'Automotive',
  'Media & Entertainment',
  'Marketing & Advertising',
  'Consulting & Professional Services',
  'Non-profit & NGO',
  'Government & Public Sector',
  'Sports & Fitness',
  'Beauty & Wellness',
  'Legal Services',
  'Agriculture & Farming',
  'Energy & Utilities',
  'Construction & Architecture',
  'Telecommunications',
  'Logistics & Supply Chain',
  'Insurance',
  'Other'
];

// Services list
const services = [
  { value: 'web-app', label: 'Web Application Development', icon: '💻' },
  { value: 'mobile-app', label: 'Mobile App Development', icon: '📱' },
  { value: 'website', label: 'Website Design & Development', icon: '🌐' },
  { value: 'ui-ux-design', label: 'UI/UX Design', icon: '🎨' },
  { value: 'ecommerce', label: 'E-commerce Solutions', icon: '🛒' },
  { value: 'digital-marketing', label: 'SEO & Digital Marketing', icon: '📈' },
  { value: 'cloud-solutions', label: 'Cloud Solutions', icon: '☁️' },
  { value: 'devops', label: 'DevOps Services', icon: '⚙️' },
  { value: 'consulting', label: 'Consulting', icon: '💼' },
  { value: 'other', label: 'Other', icon: '🔧' }
];

// Budgets list
const budgets = [
  { value: 'under-2k', label: 'Under $1,000', icon: '💵' },
  { value: '1k-2k', label: '$1,000 - $2,000', icon: '💵' },
  { value: '2k-5k', label: '$2,000 - $5,000', icon: '💰' },
  { value: '5k-8k', label: '$5,000 - $8,000', icon: '💰' },
  { value: '8k-10k', label: '$8,000 - $10,000', icon: '💎' },
  { value: '10k-plus', label: '$10,000+', icon: '💎' }
];

const Contact: React.FC = () => {
  // SEO constants from config
  const seoTitle = pageSeoData.contact.title;
  const seoDescription = pageSeoData.contact.description;
  const seoKeywords = pageSeoData.contact.keywords;
  const seoUrl = pageSeoData.contact.url;
  const seoImage = `${companyInfo.url}/images/contact-og.jpg`;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    countryCode: '',
    whatsapp: '',
    industry: '',
    service: '',
    budget: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showBudgetDropdown, setShowBudgetDropdown] = useState(false);
  
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const industryDropdownRef = useRef<HTMLDivElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);
  const budgetDropdownRef = useRef<HTMLDivElement>(null);

  // Set page title
  useEffect(() => {
    document.title = seoTitle;
  }, [seoTitle]);

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.includes(countrySearch)
  );

  // Filter industries based on search
  const filteredIndustries = industries.filter(industry =>
    industry.toLowerCase().includes(industrySearch.toLowerCase())
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(event.target as Node)) {
        setShowIndustryDropdown(false);
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target as Node)) {
        setShowServiceDropdown(false);
      }
      if (budgetDropdownRef.current && !budgetDropdownRef.current.contains(event.target as Node)) {
        setShowBudgetDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (country: typeof countries[0]) => {
    setFormData(prev => ({
      ...prev,
      country: country.name,
      countryCode: country.code
    }));
    setCountrySearch('');
    setShowCountryDropdown(false);
  };

  const handleIndustrySelect = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industry
    }));
    setIndustrySearch('');
    setShowIndustryDropdown(false);
  };

  const handleServiceSelect = (service: typeof services[0]) => {
    setFormData(prev => ({
      ...prev,
      service: service.value
    }));
    setShowServiceDropdown(false);
  };

  const handleBudgetSelect = (budget: typeof budgets[0]) => {
    setFormData(prev => ({
      ...prev,
      budget: budget.value
    }));
    setShowBudgetDropdown(false);
  };

  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      
       
      details: [
        "827, Kachpura, Kundol, Agra, Uttar Pradesh 283111"
      ]
    },
    {
      icon: FaPhone,
      
      details: ["+918826360188"]
    },
    {
      icon: FaWhatsapp,
      
      details: ["+918826360188"]
    },
    {
      icon: FaEnvelope,
     
      details: ["hello@appdeveloperpro.com"]
    },
    {
      icon: FaClock,
      
      details: ["Mon - Sat: 10:00 AM - 7:00 PM"]
    }
  ];

  const socialLinks = [
    { icon: FaFacebookF, url: "https://www.facebook.com/appdeveloperpro", label: "Facebook" },
    { icon: FaTwitter, url: "https://twitter.com", label: "Twitter" },
    { icon: FaLinkedinIn, url: "https://www.linkedin.com/company/app-developer-pro", label: "LinkedIn" },
    { icon: FaInstagram, url: "https://instagram.com", label: "Instagram" }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Format WhatsApp message
  const formatWhatsAppMessage = (data: typeof formData) => {
    const selectedService = services.find(s => s.value === data.service);
    const selectedBudget = budgets.find(b => b.value === data.budget);
    
    return `Hello App Developer Pro!

I'd like to discuss a project with you.

*Contact Details:*
- Name: ${data.name}
- Email: ${data.email}
- Company: ${data.company || 'Not specified'}
- Country: ${data.country}
- WhatsApp: ${data.countryCode}${data.whatsapp}

*Project Details:*
- Industry: ${data.industry || 'Not specified'}
- Service Required: ${selectedService?.label || 'Not specified'}
- Budget Range: ${selectedBudget?.label || 'Not specified'}

*Message:*
${data.message}

Looking forward to hearing from you!`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Format the WhatsApp message professionally
      const whatsappMessage = formatWhatsAppMessage(formData);
      
      // Your WhatsApp number
      const yourWhatsAppNumber = '918826360188';
      
      // Create WhatsApp URL
      const whatsappURL = `https://wa.me/${yourWhatsAppNumber}?text=${encodeURIComponent(whatsappMessage)}`;
      
      // Open WhatsApp in new tab
      window.open(whatsappURL, '_blank');
      
      // Show success message
      alert('Redirecting to WhatsApp... Please send the pre-filled message to complete your inquiry.');
      
      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          country: '',
          countryCode: '',
          whatsapp: '',
          industry: '',
          service: '',
          budget: '',
          message: ''
        });
        setIsSubmitting(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={seoImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="contact">
        {/* Hero Section */}
        <section className="contact__hero">
          <div className="contact__hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Get In Touch
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Have a project in mind? Let's discuss how we can help bring your ideas to life.
            </motion.p>
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="contact__main">
          <div className="contact__container">
            {/* Contact Form */}
            <motion.div
              className="contact__form-section"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="contact__form">
                {/* Two Column Grid for Input Fields */}
                <div className="contact__form-grid">
                  {/* Name */}
                  <div className="contact__form-group">
                    <label>
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="contact__form-group">
                    <label>
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  {/* Company */}
                  <div className="contact__form-group">
                    <label>Company Name</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Enter your company name"
                    />
                  </div>

                  {/* Country Dropdown */}
                  <div className="contact__form-group" ref={countryDropdownRef}>
                  <label>
                    Country <span className="required">*</span>
                  </label>
                  <div className="contact__dropdown">
                    <div 
                      className="contact__dropdown-trigger"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    >
                      {formData.country ? (
                        <span>{countries.find(c => c.name === formData.country)?.flag} {formData.country}</span>
                      ) : (
                        <span className="placeholder">Select your country</span>
                      )}
                      <FaGlobe />
                    </div>
                    {showCountryDropdown && (
                      <div className="contact__dropdown-menu">
                        <div className="contact__dropdown-search">
                          <FaSearch />
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                          />
                        </div>
                        <div className="contact__dropdown-list">
                          {filteredCountries.map((country) => (
                            <div
                              key={country.name}
                              className="contact__dropdown-item"
                              onClick={() => handleCountrySelect(country)}
                            >
                              <span>{country.flag} {country.name}</span>
                              <span className="code">{country.code}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  </div>

                  {/* WhatsApp Number */}
                  <div className="contact__form-group">
                  <label>
                    WhatsApp Number <span className="required">*</span>
                  </label>
                  <div className="contact__phone-input">
                    <span className="country-code">{formData.countryCode || '+1'}</span>
                    <input
                      type="tel"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      placeholder="Enter your WhatsApp number"
                      required
                    />
                    </div>
                  </div>

                  {/* Industry Dropdown */}
                  <div className="contact__form-group" ref={industryDropdownRef}>
                  <label>Industry</label>
                  <div className="contact__dropdown">
                    <div 
                      className="contact__dropdown-trigger"
                      onClick={() => setShowIndustryDropdown(!showIndustryDropdown)}
                    >
                      {formData.industry ? (
                        <span>{formData.industry}</span>
                      ) : (
                        <span className="placeholder">Select your industry</span>
                      )}
                      <FaBriefcase />
                    </div>
                    {showIndustryDropdown && (
                      <div className="contact__dropdown-menu">
                        <div className="contact__dropdown-search">
                          <FaSearch />
                          <input
                            type="text"
                            placeholder="Search industry..."
                            value={industrySearch}
                            onChange={(e) => setIndustrySearch(e.target.value)}
                          />
                        </div>
                        <div className="contact__dropdown-list">
                          {filteredIndustries.map((industry) => (
                            <div
                              key={industry}
                              className="contact__dropdown-item"
                              onClick={() => handleIndustrySelect(industry)}
                            >
                              {industry}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Service Dropdown */}
                  <div className="contact__form-group" ref={serviceDropdownRef}>
                  <label>
                    Service Required <span className="required">*</span>
                  </label>
                  <div className="contact__dropdown">
                    <div 
                      className="contact__dropdown-trigger"
                      onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                    >
                      {formData.service ? (
                        <span>{services.find(s => s.value === formData.service)?.icon} {services.find(s => s.value === formData.service)?.label}</span>
                      ) : (
                        <span className="placeholder">Select a service</span>
                      )}
                      <FaCog />
                    </div>
                    {showServiceDropdown && (
                      <div className="contact__dropdown-menu">
                        <div className="contact__dropdown-list">
                          {services.map((service) => (
                            <div
                              key={service.value}
                              className="contact__dropdown-item"
                              onClick={() => handleServiceSelect(service)}
                            >
                              <span>{service.icon} {service.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Budget Dropdown */}
                  <div className="contact__form-group" ref={budgetDropdownRef}>
                  <label>Budget Range</label>
                  <div className="contact__dropdown">
                    <div 
                      className="contact__dropdown-trigger"
                      onClick={() => setShowBudgetDropdown(!showBudgetDropdown)}
                    >
                      {formData.budget ? (
                        <span>{budgets.find(b => b.value === formData.budget)?.icon} {budgets.find(b => b.value === formData.budget)?.label}</span>
                      ) : (
                        <span className="placeholder">Select your budget</span>
                      )}
                      <FaDollarSign />
                    </div>
                    {showBudgetDropdown && (
                      <div className="contact__dropdown-menu">
                        <div className="contact__dropdown-list">
                          {budgets.map((budget) => (
                            <div
                              key={budget.value}
                              className="contact__dropdown-item"
                              onClick={() => handleBudgetSelect(budget)}
                            >
                              <span>{budget.icon} {budget.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="contact__form-group contact__form-group--full">
                  <label>
                    Message <span className="required">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project..."
                    rows={5}
                    required
                  />
                </div>

                {/* Submit Button */}
                <button 
                  type="submit" 
                  className="contact__form-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message <FaPaperPlane />
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              className="contact__info-section"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Contact Information</h2>
              <div className="contact__info-grid">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    className="contact__info-item"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="contact__info-icon">
                      <info.icon />
                    </div>
                    <div className="contact__info-content">
                      {info.details.map((detail, idx) => (
                        <p key={idx}>{detail}</p>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Links */}
              <div className="contact__social">
                <h3>Follow Us</h3>
                <div className="contact__social-links">
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact__social-link"
                      aria-label={social.label}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <social.icon />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Map Section - Full Width */}
        <section className="contact__map-section">
          <motion.div
            className="contact__map-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="contact__map-title">Our Location</h2>
            <p className="contact__map-description">
              Visit our office or reach out to us. We're here to help you transform your ideas into reality.
            </p>
            <div className="contact__map-wrapper">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28409.83719119861!2d78.08418948476562!3d27.117575795956018!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39746d2ba8add4a5%3A0xc98d11becb5e3f76!2sApp%20Developer%20Pro!5e0!3m2!1sen!2sin!4v1762194194075!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Our Location - 827, Kachpura, Kundol, Agra, Uttar Pradesh"
              ></iframe>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Contact;

